import { Injectable } from '@angular/core';
import { APIService, HttpParamsObject } from './api.service';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { selectAppConfigValueByDataAndResource } from '../../store/selectors/app-config.selectors';
import { selectFhirBaseUrl } from '../../store/selectors/api-config.selectors';
import { sessionAuthFeature } from '../../store/reducers/session-auth.reducer';

@Injectable({ providedIn: 'root' })
export class FhirApiService {
  constructor(
    public apiService: APIService,
    private store: Store,
  ) {}

  public read(
    resourceType: string,
    id: string,
    dataName?: string,
  ): Observable<fhir4.FhirResource> {
    return this.getBaseUrl$(dataName, resourceType).pipe(
      switchMap((baseurl) =>
        this.apiService.callGetAPI<fhir4.FhirResource>(`${baseurl}/${id}`),
      ),
    );
  }

  public update(
    resourceType: string,
    id: string,
    resource: fhir4.FhirResource,
    dataName?: string,
    headers?,
  ): Observable<fhir4.FhirResource> {
    return this.getBaseUrl$(dataName, resourceType).pipe(
      switchMap((baseurl) =>
        this.apiService.callPutAPI<fhir4.FhirResource>(
          `${baseurl}/${id}`,
          resource,
          null,
          headers,
        ),
      ),
    );
  }

  public delete(
    resourceType: string,
    id: string,
    dataName?: string,
    params?: { string: string },
  ): Observable<any> {
    return this.getBaseUrl$(dataName, resourceType).pipe(
      switchMap((baseurl) =>
        this.apiService.callDeleteAPI(`${baseurl}/${id}`, params),
      ),
    );
  }

  public create(
    resourceType: string,
    resource: fhir4.FhirResource,
    dataName?: string,
  ): Observable<fhir4.FhirResource> {
    return this.getBaseUrl$(dataName, resourceType).pipe(
      switchMap((baseurl) =>
        this.apiService.callPostAPI<fhir4.FhirResource>(baseurl, resource),
      ),
    );
  }

  public search(
    resourceType: string,
    paramsObject?: HttpParamsObject,
    dataName?: string,
  ): Observable<fhir4.Bundle> {
    return this.getBaseUrl$(dataName, resourceType).pipe(
      switchMap((baseurl) =>
        this.apiService.callGetAPI<fhir4.Bundle>(baseurl, paramsObject),
      ),
    );
  }

  public transaction(
    transactionBundle: fhir4.Bundle,
    dataName?: string,
  ): Observable<fhir4.Bundle> {
    return this.getBaseUrl$(dataName).pipe(
      switchMap((baseurl) =>
        this.apiService.callPostAPI<fhir4.Bundle>(baseurl, transactionBundle),
      ),
    );
  }

  public handleResource(
    resource: fhir4.FhirResource,
    dataName?: string,
    headers?,
  ): Observable<fhir4.FhirResource> {
    const { id, resourceType } = resource;

    if (resourceType === 'Bundle') {
      return this.transaction(resource as fhir4.Bundle, dataName);
    }

    return id
      ? this.update(resourceType, id, resource, dataName, headers)
      : this.create(resourceType, resource, dataName);
  }

  private getBaseUrl$(
    dataName?: string,
    resourceType?: string,
  ): Observable<string> {
    return this.store.pipe(
      select(
        sessionAuthFeature.selectToken,
      ) /** @todo redo to selectToken and make as param that can be requested only if token is stored */,
      filter((x) => !!x),
      take(1),
      switchMap(() =>
        this.store.pipe(
          selectAppConfigValueByDataAndResource(dataName, resourceType),
          switchMap((url) =>
            url
              ? of(url)
              : this.store.pipe(
                  selectFhirBaseUrl,
                  map((fhirBaseUrl = '') =>
                    resourceType
                      ? `${fhirBaseUrl}/${resourceType}`
                      : fhirBaseUrl,
                  ),
                ),
          ),
        ),
      ),
    );
  }
}
